import * as React from "react";
import { useLocation } from "wouter";
import { Target, Plus, Trash2, MapPin, Package, FlaskConical, CheckCircle2, RotateCcw, Clock, ArrowRight, AlertTriangle, Sparkles, Loader2, History, Flag, Zap } from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";
import { Modal } from "@/components/Modal.js";
import { useSpaces } from "@/hooks/useSpaces.js";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext.js";
import { cn } from "@/lib/utils.js";
import { InventoryPicker, type PickerItem } from "@/features/inventory/InventoryPicker.js";
import { LoadError } from "@/components/LoadError.js";
import type { GoalDetail, DiagnosisResult, PlacementSuggestion, TrialHistoryEntry, FrictionFlag } from "./types.js";
import { TRIAL_STATUS_CONFIG } from "./types.js";

/**
 * PlacementEngine — the shared "diagnose → suggest → trial → keep/put-back"
 * machinery for placement-oriented goals.
 *
 * Extracted verbatim from the old GoalDetailPage monolith so that both
 * PlacementGoalView (find_home) and ReorganizeGoalView (reorganize) compose one
 * implementation instead of duplicating it. It renders:
 *   - Gap diagnosis card
 *   - AI placement suggestion card
 *   - "Try a different spot" prompt (after a reverted trial)
 *   - Active placement trials list
 *   - Trial history (decided trials)
 *   - Create-trial, check-in, and friction-flag modals
 *
 * It owns all placement-specific state/handlers and refetches the goal via the
 * `refetchGoal` callback. No behavior change vs. the monolith.
 */
export function PlacementEngine({
  goal,
  goalId,
  isOwner,
  refetchGoal,
  onAddItems,
  itemsSlot,
}: {
  goal: GoalDetail;
  goalId: string;
  isOwner: boolean;
  refetchGoal: () => Promise<void> | void;
  /** Open the shared "Add items" modal (owned by GoalItemsPanel). */
  onAddItems: () => void;
  /** The shared items panel, rendered in its original position (between the
   *  "try a different spot" prompt and the active-trials list). */
  itemsSlot: React.ReactNode;
}) {
  const toast = useToast();
  const [, navigate] = useLocation();
  const { spaces } = useSpaces();
  const { aiEnabled } = useFeatureFlags();

  // Diagnosis
  const [diagnosis, setDiagnosis] = React.useState<DiagnosisResult | null>(null);
  const [diagnosisLoading, setDiagnosisLoading] = React.useState(false);
  const [diagnosisError, setDiagnosisError] = React.useState(false);

  // AI suggestion
  const [suggestion, setSuggestion] = React.useState<PlacementSuggestion | null>(null);
  const [suggestionLoading, setSuggestionLoading] = React.useState(false);

  // Trial history
  const [trialHistory, setTrialHistory] = React.useState<TrialHistoryEntry[]>([]);
  const [showHistory, setShowHistory] = React.useState(false);

  // Trial creation
  const [showCreateTrial, setShowCreateTrial] = React.useState(false);
  const [trialSpaceId, setTrialSpaceId] = React.useState("");
  const [trialItemIds, setTrialItemIds] = React.useState<string[]>([]);
  const [creatingTrial, setCreatingTrial] = React.useState(false);
  // Per-item destinations (F-d). When `perItemTargets` is on, each item can get
  // its own space; unset items fall back to the trial-level target (trialSpaceId).
  const [perItemTargets, setPerItemTargets] = React.useState(false);
  // "Try for 2 weeks" (false, default) vs "Make this the home" (true) — F-b.
  // Permanent placements move items now and adopt immediately (no review clock).
  const [trialPermanent, setTrialPermanent] = React.useState(false);
  // P0-A: the default action is a PLAIN MOVE ("Move it here"), no trial. Only when
  // the user opts into "Try it for 2 weeks" does trial ceremony appear (tryMode=true).
  const [tryMode, setTryMode] = React.useState(false);
  const [movingNow, setMovingNow] = React.useState(false);
  // "Move later" (§6b): decided where it goes, not moving it right now.
  const [planningLater, setPlanningLater] = React.useState(false);
  const [itemTargets, setItemTargets] = React.useState<Record<string, string>>({});
  const [suggestedSpaces, setSuggestedSpaces] = React.useState<{ spaceId: string; spaceName: string; reason: string; itemCount: number }[]>([]);
  const [currentLocationIsBest, setCurrentLocationIsBest] = React.useState(false);
  const [currentLocationBest, setCurrentLocationBest] = React.useState<{ spaceId: string; spaceName: string; reason: string } | null>(null);
  const [suggestedSpacesLoading, setSuggestedSpacesLoading] = React.useState(false);
  const [showAllSpaces, setShowAllSpaces] = React.useState(false);

  // Check-in
  const [showCheckIn, setShowCheckIn] = React.useState<string | null>(null);
  const [outcomeNote, setOutcomeNote] = React.useState("");
  const [checkInFlags, setCheckInFlags] = React.useState<FrictionFlag[]>([]);
  const [checkInTrialDetail, setCheckInTrialDetail] = React.useState<{
    items: { itemId: string; itemName: string; originalSpaceId: string | null; originalSpaceName: string | null; currentSpaceId: string | null }[];
    targetSpace: { id: string; name: string } | null;
    startedAt: string | null;
    reviewAfter: string | null;
  } | null>(null);
  const [checkInStep, setCheckInStep] = React.useState<"decide" | "confirm-adopt" | "confirm-revert">("decide");

  // Confirm-move confirmation
  const [confirmMoveTrialId, setConfirmMoveTrialId] = React.useState<string | null>(null);

  // Mutation pending states (prevent double-submit)
  const [confirmMovePending, setConfirmMovePending] = React.useState(false);
  const [adoptPending, setAdoptPending] = React.useState(false);
  const [revertPending, setRevertPending] = React.useState(false);
  const [extendPending, setExtendPending] = React.useState(false);

  // Friction flag
  const [showFrictionFlag, setShowFrictionFlag] = React.useState<string | null>(null);
  const [frictionNote, setFrictionNote] = React.useState("");
  const [flagging, setFlagging] = React.useState(false);

  // Goal → routine handoff (offered after a trial is kept). One-way, optional:
  // creates a STANDALONE routine to keep the new arrangement maintained. The
  // routine is independent of the goal (deleting the goal never deletes it).
  const [showRoutineHandoff, setShowRoutineHandoff] = React.useState(false);
  const [routineTitle, setRoutineTitle] = React.useState("");
  const [routineFrequency, setRoutineFrequency] = React.useState("weekly");
  const [creatingRoutine, setCreatingRoutine] = React.useState(false);

  const fetchDiagnosis = React.useCallback(async () => {
    if (!goalId) return;
    setDiagnosisLoading(true);
    setDiagnosisError(false);
    try {
      const data = await apiFetch<DiagnosisResult>(`/api/organizing-goals/${goalId}/diagnosis`);
      setDiagnosis(data);
    } catch {
      setDiagnosisError(true);
    }
    setDiagnosisLoading(false);
  }, [goalId]);

  const fetchTrialHistory = React.useCallback(async () => {
    if (!goalId) return;
    try {
      const data = await apiFetch<TrialHistoryEntry[]>(`/api/organizing-goals/${goalId}/trials`);
      setTrialHistory(data);
    } catch { /* ignore */ }
  }, [goalId]);

  // Auto-fetch diagnosis when goal has items and an activity location
  React.useEffect(() => {
    if (!goal || goal.items.length === 0) return;
    fetchDiagnosis();
  }, [goal.id, goal.items.length, goal.activityLocationId, fetchDiagnosis]);

  // Fetch trial history
  React.useEffect(() => {
    if (!goalId) return;
    fetchTrialHistory();
  }, [goalId, fetchTrialHistory]);

  // Fetch suggested spaces when the create trial modal opens
  React.useEffect(() => {
    if (!showCreateTrial || !goalId) return;
    setSuggestedSpacesLoading(true);
    setShowAllSpaces(false);
    setCurrentLocationIsBest(false);
    setCurrentLocationBest(null);
    apiFetch<{
      suggestions: { spaceId: string; spaceName: string; reason: string; itemCount: number }[];
      currentLocationIsBest: boolean;
      currentLocationBest: { spaceId: string; spaceName: string; reason: string } | null;
    }>(`/api/organizing-goals/${goalId}/suggested-spaces`)
      .then((data) => {
        setSuggestedSpaces(data.suggestions ?? []);
        setCurrentLocationIsBest(Boolean(data.currentLocationIsBest));
        setCurrentLocationBest(data.currentLocationBest ?? null);
      })
      .catch(() => {
        setSuggestedSpaces([]);
        setCurrentLocationIsBest(false);
        setCurrentLocationBest(null);
      })
      .finally(() => setSuggestedSpacesLoading(false));
  }, [showCreateTrial, goalId]);

  // Refetch everything owned by the parent + engine after a mutation.
  const refetchAll = React.useCallback(async () => {
    await refetchGoal();
    await fetchTrialHistory();
    await fetchDiagnosis();
  }, [refetchGoal, fetchTrialHistory, fetchDiagnosis]);

  // --- AI Suggest Placement ---
  const handleSuggestPlacement = async () => {
    if (!goalId) return;
    setSuggestionLoading(true);
    try {
      const data = await apiFetch<PlacementSuggestion>("/api/ai/suggest-placement", {
        method: "POST",
        body: JSON.stringify({ goalId }),
      });
      setSuggestion(data);
    } catch (err: any) {
      toast.show(err.message?.includes("limit") ? "AI action limit reached" : "Failed to get suggestion", "error");
    }
    setSuggestionLoading(false);
  };

  const handleAcceptSuggestion = () => {
    if (!suggestion?.suggestedSpaceId) return;
    setTrialSpaceId(suggestion.suggestedSpaceId);
    setTrialItemIds(goal.items.map((gi) => gi.itemId));
    setSuggestion(null);
    setShowCreateTrial(true);
  };

  const handleCreateTrial = async () => {
    if (!trialSpaceId || trialItemIds.length === 0 || creatingTrial) return;
    setCreatingTrial(true);
    try {
      // F-d: build per-item targets only for selected items whose chosen space
      // differs from the trial-level target. Anything omitted falls back to it,
      // so a normal single-destination trial sends no itemTargets at all.
      const body: Record<string, unknown> = { targetSpaceId: trialSpaceId, itemIds: trialItemIds };
      if (perItemTargets) {
        const overrides: Record<string, string> = {};
        for (const itemId of trialItemIds) {
          const dest = itemTargets[itemId];
          if (dest && dest !== trialSpaceId) overrides[itemId] = dest;
        }
        if (Object.keys(overrides).length > 0) body.itemTargets = overrides;
      }
      // F-b: "Make this the home" adopts immediately instead of starting a trial.
      if (trialPermanent) body.permanent = true;
      const created = await apiFetch<{ focusNudge?: { activeCount: number; message: string } | null }>(
        `/api/organizing-goals/${goalId}/trials`,
        { method: "POST", body: JSON.stringify(body) },
      );
      const wasPermanent = trialPermanent;
      toast.show(
        wasPermanent
          ? "Done — those items now live in their new home."
          : "Trial created! Move the items when you're ready.",
        "success",
      );
      // Soft focus nudge (R-A8): if a lot is in motion, gently suggest deciding
      // on one first. Never a block — the trial was already created.
      if (!wasPermanent && created?.focusNudge) {
        toast.show(created.focusNudge.message, "warning");
      }
      setShowCreateTrial(false);
      setTrialSpaceId("");
      setTrialItemIds([]);
      setPerItemTargets(false);
      setItemTargets({});
      setTrialPermanent(false);
      setTryMode(false);
      await refetchGoal();
      await fetchTrialHistory();
      // A permanent placement is an immediate adopt — offer the same one-way
      // goal→routine handoff the trial-keep path offers, so upkeep still gets set up.
      if (wasPermanent) {
        setRoutineTitle(`Keep ${goal.title} tidy`.slice(0, 200));
        setRoutineFrequency("weekly");
        setShowRoutineHandoff(true);
      }
    } catch (err: any) {
      toast.show(err.message || "Failed to create trial", "error");
    }
    setCreatingTrial(false);
  };

  // P0-A default: a plain "Move it here". Writes item_history only (no trial),
  // shows an Undo toast that restores the previous per-item locations honestly
  // (recorded as a move + revert in history, never a silent mutation).
  const handleMove = async () => {
    if (!trialSpaceId || trialItemIds.length === 0 || movingNow) return;
    setMovingNow(true);
    try {
      // Snapshot each item's current location so Undo can restore it exactly.
      const prior: Record<string, string | null> = {};
      // Also snapshot which items were flagged Re-locate — the move clears the
      // flag (a confirmed home, R-B5), so Undo must restore it to be a true no-op.
      const priorFlagged: string[] = [];
      for (const id of trialItemIds) {
        const gi = goal.items.find((g) => g.itemId === id);
        prior[id] = gi?.itemSpaceId ?? null;
        if (gi?.itemRelocateAt) priorFlagged.push(id);
      }
      // Move: a plain bulk relocation. The server writes a "moved" item_history
      // row per item and clears any Re-locate flag (a confirmed home, R-B5).
      await apiFetch(`/api/items/bulk`, {
        method: "PUT",
        body: JSON.stringify({ ids: trialItemIds, spaceId: trialSpaceId }),
      });
      const targetName = spaces.find((s) => s.id === trialSpaceId)?.name ?? "there";
      const movedIds = [...trialItemIds];
      setShowCreateTrial(false);
      setTrialSpaceId("");
      setTrialItemIds([]);
      setTryMode(false);
      setPerItemTargets(false);
      setItemTargets({});
      await refetchGoal();
      // Undo (R-A5): restore each item to its prior location, grouped by prior
      // space so one bulk call per group. This is a real move back — the server
      // records it as another "moved" event, so history reads move → revert.
      toast.show(`Moved to ${targetName}`, "success", {
        label: "Undo",
        onClick: async () => {
          try {
            const byPrior = new Map<string | null, string[]>();
            for (const id of movedIds) {
              const p = prior[id] ?? null;
              const arr = byPrior.get(p) ?? [];
              arr.push(id);
              byPrior.set(p, arr);
            }
            for (const [spaceId, ids] of byPrior) {
              await apiFetch(`/api/items/bulk`, {
                method: "PUT",
                body: JSON.stringify({ ids, spaceId }),
              });
            }
            // Restore any Re-locate flag the forward move cleared, so move→undo
            // is a true no-op (R-B5 fidelity).
            for (const id of priorFlagged) {
              await apiFetch(`/api/items/${id}/relocate`, { method: "PUT", body: JSON.stringify({ relocate: true }) });
            }
            toast.show("Put back where it was.", "success");
            await refetchGoal();
          } catch {
            toast.show("Couldn't undo the move", "error");
          }
        },
      });
    } catch (err: any) {
      toast.show(err.message || "Failed to move items", "error");
    }
    setMovingNow(false);
  };

  // "Move later" (§6b): record the decided destination as a planned move. The
  // items stay where they are (their location stays honest) and show up in
  // "Moves to make" until the user confirms they've moved them.
  const handleMoveLater = async () => {
    if (!trialSpaceId || trialItemIds.length === 0 || planningLater) return;
    setPlanningLater(true);
    try {
      const res = await apiFetch<{ planned: number; skipped: string[] }>(`/api/items/planned-moves`, {
        method: "PUT",
        body: JSON.stringify({ moves: trialItemIds.map((itemId) => ({ itemId, spaceId: trialSpaceId })) }),
      });
      const targetName = spaces.find((s) => s.id === trialSpaceId)?.name ?? "there";
      setShowCreateTrial(false);
      setTrialSpaceId("");
      setTrialItemIds([]);
      setTryMode(false);
      setPerItemTargets(false);
      setItemTargets({});
      await refetchGoal();
      if (res.planned === 0) {
        toast.show(`Already in ${targetName} — nothing to move.`, "success");
      } else {
        toast.show(`Added to Moves to make — ${targetName}`, "success", {
          label: "View",
          onClick: () => navigate("/organize/moves"),
        });
      }
    } catch (err: any) {
      toast.show(err.message || "Couldn't save the move", "error");
    }
    setPlanningLater(false);
  };

  const handleConfirmMove = async (trialId: string) => {
    if (confirmMovePending) return;
    setConfirmMovePending(true);
    try {
      await apiFetch(`/api/placement-trials/${trialId}/confirm-move`, { method: "PUT", body: JSON.stringify({}) });
      toast.show("Clock started! We'll check in with you in 2 weeks.", "success");
      await refetchAll();
    } catch (err: any) {
      toast.show(err.message || "Failed to confirm move", "error");
    }
    setConfirmMovePending(false);
  };

  const handleAdopt = async (trialId: string) => {
    if (adoptPending) return;
    setAdoptPending(true);
    try {
      await apiFetch(`/api/placement-trials/${trialId}/adopt`, {
        method: "PUT",
        body: JSON.stringify({ outcomeNote: outcomeNote.trim() || null }),
      });
      toast.show("Kept! Those items now live in their new home.", "success");
      setShowCheckIn(null);
      setOutcomeNote("");
      setCheckInStep("decide");
      setCheckInTrialDetail(null);
      await refetchAll();
      // One-way handoff: offer a routine to keep the new arrangement maintained.
      setRoutineTitle(`Keep ${goal.title} tidy`.slice(0, 200));
      setRoutineFrequency("weekly");
      setShowRoutineHandoff(true);
    } catch (err: any) {
      toast.show(err.message || "Failed to adopt", "error");
    }
    setAdoptPending(false);
  };

  const handleCreateRoutineFromGoal = async () => {
    if (!routineTitle.trim() || creatingRoutine) return;
    setCreatingRoutine(true);
    try {
      await apiFetch(`/api/organizing-goals/${goalId}/create-routine`, {
        method: "POST",
        body: JSON.stringify({
          title: routineTitle.trim(),
          frequency: routineFrequency,
          tasks: [{ id: "1", label: routineTitle.trim(), isCompleted: false }],
        }),
      });
      toast.show("Routine created! Find it in Routines.", "success");
      setShowRoutineHandoff(false);
    } catch (err: any) {
      toast.show(err.message || "Failed to create routine", "error");
    }
    setCreatingRoutine(false);
  };

  const handleRevert = async (trialId: string) => {
    if (revertPending) return;
    setRevertPending(true);
    try {
      const result = await apiFetch<{ revertSummary?: { itemCount: number; unplacedCount: number } }>(`/api/placement-trials/${trialId}/revert`, {
        method: "PUT",
        body: JSON.stringify({ outcomeNote: outcomeNote.trim() || null }),
      });
      // Be honest: some items may have had no original spot to return to (their space
      // was deleted mid-trial) and are now unplaced — don't claim "original spots".
      const s = result?.revertSummary;
      const msg = s && s.unplacedCount > 0
        ? `Put back — ${s.itemCount - s.unplacedCount} returned, ${s.unplacedCount} had no original spot and ${s.unplacedCount === 1 ? "is" : "are"} now unplaced.`
        : "Put back! Items returned to their original spots.";
      toast.show(msg, "success");
      setShowCheckIn(null);
      setOutcomeNote("");
      setCheckInStep("decide");
      setCheckInTrialDetail(null);
      await refetchAll();
    } catch (err: any) {
      toast.show(err.message || "Failed to revert", "error");
    }
    setRevertPending(false);
  };

  const handleExtend = async (trialId: string) => {
    if (extendPending) return;
    setExtendPending(true);
    try {
      await apiFetch(`/api/placement-trials/${trialId}/extend`, {
        method: "PUT",
        body: JSON.stringify({ days: 14 }),
      });
      toast.show("Extended by 2 more weeks.", "success");
      setShowCheckIn(null);
      setCheckInStep("decide");
      setCheckInTrialDetail(null);
      await refetchAll();
    } catch (err: any) {
      toast.show(err.message || "Failed to extend", "error");
    }
    setExtendPending(false);
  };

  // Open check-in and load friction flags + trial detail for context
  const openCheckIn = async (trialId: string) => {
    setShowCheckIn(trialId);
    setCheckInStep("decide");
    setCheckInTrialDetail(null);
    try {
      const [flags, detail] = await Promise.all([
        apiFetch<FrictionFlag[]>(`/api/placement-trials/${trialId}/friction`),
        apiFetch<any>(`/api/placement-trials/${trialId}`),
      ]);
      setCheckInFlags(flags);
      setCheckInTrialDetail({
        items: detail.items ?? [],
        targetSpace: detail.targetSpace ?? null,
        startedAt: detail.startedAt ?? null,
        reviewAfter: detail.reviewAfter ?? null,
      });
    } catch {
      setCheckInFlags([]);
      setCheckInTrialDetail(null);
    }
  };

  // Friction flag submission
  const handleSubmitFriction = async (trialId: string) => {
    setFlagging(true);
    try {
      await apiFetch(`/api/placement-trials/${trialId}/friction`, {
        method: "POST",
        body: JSON.stringify({ note: frictionNote.trim() || null }),
      });
      toast.show("Feedback sent to the trial owner.", "success");
      setShowFrictionFlag(null);
      setFrictionNote("");
    } catch (err: any) {
      toast.show(err.message || "Failed to submit feedback", "error");
    }
    setFlagging(false);
  };

  // Sequential trial context: show "try different spot" if the most recent trial was reverted
  const mostRecentTrial = trialHistory[0]; // already ordered by createdAt DESC
  const lastRevertedTrial = mostRecentTrial?.status === "reverted" ? mostRecentTrial : null;
  const hasActiveTrials = goal.activeTrials.length > 0;
  const showTryDifferentSpot = !hasActiveTrials && lastRevertedTrial && goal.status === "active" && goal.items.length > 0;

  // Past decided trials for history display
  const decidedTrials = trialHistory.filter((t) => ["adopted", "reverted", "expired_not_started"].includes(t.status));

  // Map the goal's linked items into the shared InventoryPicker's item shape so
  // the trial "items to move" list reuses the same filter/selection UX (F-c).
  const trialPickerItems: PickerItem[] = React.useMemo(
    () => goal.items.map((gi) => ({
      id: gi.itemId,
      name: gi.itemName,
      quantity: 1,
      spaceId: gi.itemSpaceId,
      spaceName: gi.spaceName,
      tags: [],
      photoUrl: null,
    })),
    [goal.items]
  );

  return (
    <>
      {/* Gap diagnosis failed to load (only when we have items to diagnose and no data yet) */}
      {diagnosisError && !diagnosis && goal.items.length > 0 && (
        <LoadError variant="inline" message="Couldn't load the gap diagnosis." onRetry={fetchDiagnosis} retrying={diagnosisLoading} />
      )}
      {/* Gap Diagnosis */}
      {diagnosis && diagnosis.totalCount > 0 && (
        <div className="bg-white rounded-lg border border-clay-border p-5">
          <h2 className="font-semibold text-ink flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-primary" />Gap diagnosis
            {diagnosisLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-faint" />}
          </h2>
          {/* Summary */}
          <div className="flex items-center gap-3 p-3 rounded-md bg-amethyst-soft border border-clay-border mb-3">
            <div className="flex-1">
              <p className="text-sm font-medium text-body">{diagnosis.summary}</p>
            </div>
            {diagnosis.activityLocation && (
              <div className="text-right">
                <div className="text-2xl font-bold text-primary tabular-nums">{diagnosis.inPlaceCount}/{diagnosis.totalCount}</div>
                <div className="text-[0.8125rem] text-body">in place</div>
              </div>
            )}
          </div>
          {/* Item breakdown */}
          {diagnosis.activityLocation && (
            <div className="space-y-1">
              {diagnosis.items.map((item) => (
                <div key={item.id} className="flex items-center gap-2 py-1.5 px-2 rounded-md text-sm">
                  <div className={`w-2 h-2 rounded-full ${item.inPlace ? "bg-sage-deep" : "bg-warning"}`} />
                  <span className="flex-1 text-body">{item.name}</span>
                  <span className="text-xs text-faint">
                    {item.currentSpace ? item.currentSpace.name : "Unplaced"}
                  </span>
                  {item.inPlace && <CheckCircle2 className="w-3.5 h-3.5 text-sage-deep" />}
                </div>
              ))}
            </div>
          )}
          {!diagnosis.activityLocation && (
            <div className="mt-2 p-3 rounded-md bg-paper-raised border border-clay-border">
              <p className="text-xs text-body mb-2">Set an activity location to see which items need to move.</p>
              {isOwner && (
                <select
                  value=""
                  onChange={async (e) => {
                    if (!e.target.value) return;
                    try {
                      await apiFetch(`/api/organizing-goals/${goalId}`, {
                        method: "PUT",
                        body: JSON.stringify({ activityLocationId: e.target.value }),
                      });
                      toast.show("Activity location set!", "success");
                      await refetchGoal();
                      await fetchDiagnosis();
                    } catch (err: any) {
                      toast.show(err.message || "Failed to set location", "error");
                    }
                  }}
                  className="w-full px-3 py-2 border border-clay-border rounded-sm text-xs focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white text-body"
                >
                  <option value="">Choose activity location…</option>
                  {spaces.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>
      )}

      {/* AI Placement Suggestion */}
      {aiEnabled && goal.status === "active" && goal.items.length > 0 && !hasActiveTrials && (
        <div className="bg-white rounded-lg border border-clay-border p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-ink flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-sage-deep" />AI suggestion
            </h2>
            <button
              onClick={handleSuggestPlacement}
              disabled={suggestionLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-sage-wash text-sage-deep text-xs font-medium hover:bg-sage-wash/70 transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed"
            >
              {suggestionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {suggestion ? "Try again" : "Suggest a spot"}
            </button>
          </div>

          {suggestion && (
            <div className="space-y-3">
              <div className="p-3 rounded-md bg-sage-wash border border-clay-border">
                {suggestion.suggestedSpaceName ? (
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="w-4 h-4 text-sage-deep" />
                    <span className="font-medium text-sm text-ink">{suggestion.suggestedSpaceName}</span>
                  </div>
                ) : null}
                <p className="text-sm text-body">{suggestion.reasoning}</p>
              </div>
              {suggestion.suggestedSpaceId && (
                <div className="flex gap-2">
                  <button
                    onClick={handleAcceptSuggestion}
                    className="flex-1 py-2 px-3 bg-primary text-white rounded-md text-xs font-medium hover:bg-amethyst-ink transition-colors"
                  >
                    Start trial here
                  </button>
                  <button
                    onClick={() => setSuggestion(null)}
                    className="py-2 px-3 bg-amethyst-soft text-amethyst-ink rounded-md text-xs font-medium hover:bg-amethyst-soft/70 transition-colors"
                  >
                    Dismiss
                  </button>
                </div>
              )}
            </div>
          )}

          {!suggestion && !suggestionLoading && (
            <p className="text-xs text-faint">
              Get an AI-powered suggestion for where to try placing your items based on your goal and home context.
            </p>
          )}
        </div>
      )}

      {/* Sequential trial prompt: "Try a different spot" */}
      {showTryDifferentSpot && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-amethyst-soft border border-clay-border">
          <RotateCcw className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amethyst-ink">
              Your last trial didn't stick — want to try a different spot?
            </p>
            {lastRevertedTrial?.outcomeNote && (
              <p className="text-xs text-body mt-1 italic">"{lastRevertedTrial.outcomeNote}"</p>
            )}
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => {
                  setTrialItemIds(goal.items.map((gi) => gi.itemId));
                  setShowCreateTrial(true);
                }}
                className="px-3 py-1.5 rounded-md text-xs font-medium bg-primary text-white hover:bg-amethyst-ink transition-colors"
              >
                Try a different spot
              </button>
              {aiEnabled && (
                <button
                  onClick={handleSuggestPlacement}
                  disabled={suggestionLoading}
                  className="px-3 py-1.5 rounded-md text-xs font-medium bg-white border border-clay-border text-amethyst-ink hover:bg-amethyst-soft/70 transition-colors flex items-center gap-1"
                >
                  <Sparkles className="w-3 h-3" />Get AI suggestion
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Items section (rendered here to match the original layout order) */}
      {itemsSlot}

      {/* Active trials section */}
      <div className="bg-white rounded-lg border border-clay-border p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-ink flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-faint" />Placement trials
          </h2>
          {goal.items.length > 0 && isOwner && (
            <button
              onClick={() => {
                setTrialItemIds(goal.items.map((gi) => gi.itemId));
                setShowCreateTrial(true);
              }}
              className="flex items-center gap-1 text-xs text-primary font-medium hover:text-amethyst-ink py-2"
            >
              <Plus className="w-3.5 h-3.5" />Try a placement
            </button>
          )}
        </div>

        {goal.activeTrials.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-faint mb-3">
              {goal.items.length === 0
                ? "Add items that are part of this activity, then try moving them to a new spot."
                : "Ready to try a new arrangement? Pick a space and give it a couple weeks."}
            </p>
            {goal.items.length === 0 ? (
              <button
                onClick={onAddItems}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-amethyst-soft text-primary rounded-md text-sm font-medium hover:bg-amethyst-soft/70 transition-colors"
              >
                <Plus className="w-4 h-4" />Add items to get started
              </button>
            ) : isOwner ? (
              <button
                onClick={() => {
                  setTrialItemIds(goal.items.map((gi) => gi.itemId));
                  setShowCreateTrial(true);
                }}
                className="text-sm text-primary font-medium hover:text-amethyst-ink"
              >
                Start a trial →
              </button>
            ) : null}
          </div>
        ) : (
          <div className="space-y-3">
            {goal.activeTrials.map((trial) => {
              const statusConfig = TRIAL_STATUS_CONFIG[trial.status] ?? TRIAL_STATUS_CONFIG.created;
              const targetSpace = spaces.find((s) => s.id === trial.targetSpaceId);
              const isActive = ["confirmed_moved", "in_progress"].includes(trial.status);
              const isAwaiting = trial.status === "created";
              const trialFrictionCount = trialHistory.find((t) => t.id === trial.id)?.frictionFlagCount ?? 0;

              return (
                <div key={trial.id} className="p-4 rounded-md border border-clay-border space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded-full text-[0.8125rem] font-medium ${statusConfig.color}`}>
                        {statusConfig.label}
                      </span>
                      {targetSpace && (
                        <span className="text-xs text-body flex items-center gap-1">
                          <ArrowRight className="w-3 h-3" />{targetSpace.name}
                        </span>
                      )}
                      {trialFrictionCount > 0 && (
                        <span className="text-xs text-warning-text flex items-center gap-0.5">
                          <AlertTriangle className="w-3 h-3" /><span className="tabular-nums">{trialFrictionCount}</span> concern{trialFrictionCount !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                    {trial.reviewAfter && (
                      <span className="text-[0.8125rem] text-faint flex items-center gap-1 tabular-nums">
                        <Clock className="w-3 h-3" />Review {new Date(trial.reviewAfter).toLocaleDateString()}
                      </span>
                    )}
                  </div>

                  {/* Day counter + progress bar for active trials */}
                  {isActive && trial.startedAt && trial.reviewAfter && (() => {
                    const startDate = new Date(trial.startedAt);
                    const reviewDate = new Date(trial.reviewAfter);
                    const now = new Date();
                    const totalDays = Math.max(1, Math.round((reviewDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)));
                    const elapsedDays = Math.max(0, Math.round((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)));
                    const progress = Math.min(1, elapsedDays / totalDays);
                    const isPastReview = now >= reviewDate;

                    return (
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-medium text-body tabular-nums">
                            {isPastReview
                              ? "Time to decide"
                              : `Day ${elapsedDays} of ${totalDays}`}
                          </span>
                          <span className="text-faint tabular-nums">
                            {isPastReview
                              ? "Review period reached"
                              : `${totalDays - elapsedDays} day${totalDays - elapsedDays !== 1 ? "s" : ""} left`}
                          </span>
                        </div>
                        <div className="h-1.5 bg-clay-muted rounded-full overflow-hidden">
                          <div
                            className={cn(
                              "h-full rounded-full transition-all",
                              isPastReview ? "bg-warning" : "bg-primary"
                            )}
                            style={{ width: `${Math.round(progress * 100)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })()}

                  {/* Actions */}
                  <div className="flex gap-2">
                    {isAwaiting && isOwner && (
                      confirmMoveTrialId === trial.id ? (
                        <div className="flex-1 flex gap-2">
                          <button
                            onClick={() => { handleConfirmMove(trial.id); setConfirmMoveTrialId(null); }}
                            disabled={confirmMovePending}
                            className="flex-1 py-2 px-3 bg-primary text-white rounded-md text-xs font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed"
                          >
                            {confirmMovePending ? "Confirming..." : "Yes, I've moved them"}
                          </button>
                          <button
                            onClick={() => setConfirmMoveTrialId(null)}
                            className="py-2 px-3 bg-amethyst-soft text-amethyst-ink rounded-md text-xs font-medium hover:bg-amethyst-soft/70 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmMoveTrialId(trial.id)}
                          className="flex-1 py-2 px-3 bg-amethyst-soft text-amethyst-ink rounded-md text-xs font-medium hover:bg-amethyst-soft/70 transition-colors"
                        >
                          I've moved them ✓
                        </button>
                      )
                    )}
                    {isActive && isOwner && (
                      <button
                        onClick={() => openCheckIn(trial.id)}
                        className="flex-1 py-2 px-3 bg-amethyst-soft text-amethyst-ink rounded-md text-xs font-medium hover:bg-amethyst-soft/70 transition-colors"
                      >
                        {trial.reviewAfter && new Date(trial.reviewAfter) <= new Date()
                          ? "Time to decide"
                          : "Check in early"}
                      </button>
                    )}
                    {/* Friction flag for non-owners */}
                    {isActive && !isOwner && (
                      <button
                        onClick={() => setShowFrictionFlag(trial.id)}
                        className="flex-1 py-2 px-3 bg-warning-wash text-warning-text rounded-md text-xs font-medium hover:bg-warning-wash/70 transition-colors flex items-center justify-center gap-1"
                      >
                        <Flag className="w-3 h-3" />This isn't working for me
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Trial History */}
      {decidedTrials.length > 0 && (
        <div className="bg-white rounded-lg border border-clay-border p-5">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center justify-between w-full"
          >
            <h2 className="font-semibold text-ink flex items-center gap-2">
              <History className="w-4 h-4 text-faint" />Trial history (<span className="tabular-nums">{decidedTrials.length}</span>)
            </h2>
            <span className="text-xs text-faint">{showHistory ? "Hide" : "Show"}</span>
          </button>

          {showHistory && (
            <div className="mt-3 space-y-2">
              {decidedTrials.map((trial) => {
                const statusConfig = TRIAL_STATUS_CONFIG[trial.status] ?? TRIAL_STATUS_CONFIG.created;
                return (
                  <div key={trial.id} className="p-3 rounded-md bg-paper-raised border border-clay-border space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-[0.8125rem] font-medium ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                        <span className="text-xs text-body">
                          {trial.targetSpaceName ?? "Unknown space"}
                        </span>
                        {trial.frictionFlagCount > 0 && (
                          <span className="text-[0.8125rem] text-warning-text flex items-center gap-0.5 tabular-nums">
                            <AlertTriangle className="w-2.5 h-2.5" />{trial.frictionFlagCount}
                          </span>
                        )}
                      </div>
                      <span className="text-[0.8125rem] text-faint tabular-nums">
                        {trial.decidedAt ? new Date(trial.decidedAt).toLocaleDateString() : ""}
                      </span>
                    </div>
                    {trial.outcomeNote && (
                      <p className="text-xs text-body italic pl-1">"{trial.outcomeNote}"</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Give these items a home (P0-A). Default = a plain "Move it here" (one
          tap, no trial). Trial ceremony (the 14-day timeline) appears ONLY after
          the user opts into "Try it for 2 weeks" (tryMode). */}
      <Modal open={showCreateTrial} onClose={() => { setShowCreateTrial(false); setPerItemTargets(false); setItemTargets({}); setTrialPermanent(false); setTryMode(false); }} title="Give these items a home">
        <div className="space-y-4">
          {/* Trial ceremony — shown ONLY when the user opted into "Try it" (R-A3).
              A plain move never sees the 14-day clock or timeline. */}
          {tryMode && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-amethyst-soft border border-clay-border">
              <div className="flex items-center gap-1 text-xs text-body">
                <Package className="w-3.5 h-3.5 text-primary" />
                <span className="font-medium">Items move now</span>
              </div>
              <ArrowRight className="w-3 h-3 text-faint" />
              <div className="flex items-center gap-1 text-xs text-body">
                <Clock className="w-3.5 h-3.5 text-primary" />
                <span className="font-medium">14-day trial</span>
              </div>
              <ArrowRight className="w-3 h-3 text-faint" />
              <div className="flex items-center gap-1 text-xs text-body">
                <CheckCircle2 className="w-3.5 h-3.5 text-sage-deep" />
                <span className="font-medium">Keep or put back</span>
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-body mb-2 block">Move to</label>

            {/* Current-location-is-best signal */}
            {!suggestedSpacesLoading && currentLocationIsBest && (
              <div className="mb-3 flex items-start gap-2.5 p-3 rounded-md bg-warning-wash border border-clay-border">
                <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
                <div className="text-xs text-warning-text space-y-1">
                  <p className="font-medium">
                    {currentLocationBest?.spaceName ? `${currentLocationBest.spaceName} still looks like the best spot.` : "The current spot still looks best."}
                  </p>
                  <p className="text-warning-text">
                    We don't have a better place to suggest right now. If it isn't working, tell us what's wrong
                    (use "Report an issue" on the trial), or add another container or space to compare against —
                    then try again.
                  </p>
                </div>
              </div>
            )}

            {/* Suggested spaces (ranked cards) */}
            {suggestedSpacesLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                <span className="text-xs text-faint ml-2">Finding best spots...</span>
              </div>
            ) : suggestedSpaces.length > 0 && !showAllSpaces ? (
              <div className="space-y-2">
                {suggestedSpaces.slice(0, 3).map((suggestion) => (
                  <button
                    key={suggestion.spaceId}
                    onClick={() => setTrialSpaceId(suggestion.spaceId)}
                    className={cn(
                      "w-full text-left p-3 rounded-md border transition-colors",
                      trialSpaceId === suggestion.spaceId
                        ? "border-primary bg-amethyst-soft"
                        : "border-clay-border hover:border-primary/40"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-ink">{suggestion.spaceName}</span>
                      {trialSpaceId === suggestion.spaceId && (
                        <CheckCircle2 className="w-4 h-4 text-primary" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-body">{suggestion.reason}</span>
                      {suggestion.itemCount > 0 && (
                        <span className="text-[0.8125rem] text-faint tabular-nums">· {suggestion.itemCount} item{suggestion.itemCount !== 1 ? "s" : ""} there</span>
                      )}
                    </div>
                  </button>
                ))}
                <button
                  onClick={() => setShowAllSpaces(true)}
                  className="w-full text-center py-2 text-xs text-faint hover:text-primary transition-colors"
                >
                  Choose a different space…
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {suggestedSpaces.length > 0 && showAllSpaces && (
                  <button
                    onClick={() => setShowAllSpaces(false)}
                    className="text-xs text-primary hover:text-amethyst-ink mb-1"
                  >
                    ← Back to suggestions
                  </button>
                )}
                <select
                  value={trialSpaceId}
                  onChange={(e) => setTrialSpaceId(e.target.value)}
                  className="w-full px-4 py-3 border border-clay-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white text-body"
                >
                  <option value="">Choose a space...</option>
                  {spaces.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* From→To summary */}
          {trialSpaceId && trialItemIds.length > 0 && (() => {
            const targetSpace = spaces.find((s) => s.id === trialSpaceId);
            const sourceSpaces = [...new Set(
              goal.items
                .filter((gi) => trialItemIds.includes(gi.itemId))
                .map((gi) => gi.spaceName || "Unplaced")
            )];
            return (
              <div className="flex items-center gap-2 p-3 rounded-md bg-paper-raised text-xs text-body">
                <Package className="w-3.5 h-3.5 text-faint shrink-0" />
                <span>Moving <strong className="tabular-nums">{trialItemIds.length} item{trialItemIds.length !== 1 ? "s" : ""}</strong> from {sourceSpaces.join(", ")}</span>
                <ArrowRight className="w-3 h-3 text-faint shrink-0" />
                <strong>{targetSpace?.name}</strong>
              </div>
            );
          })()}

          <div>
            <label className="text-xs font-medium text-body mb-2 block">Items to move (<span className="tabular-nums">{trialItemIds.length}</span>)</label>
            {/* Shared InventoryPicker (F-c) in client mode: the candidate set is
                the goal's already-linked items, with the same search/location/tag
                filters as the inventory list. */}
            <InventoryPicker
              items={trialPickerItems}
              selectedIds={trialItemIds}
              onChange={setTrialItemIds}
              listMaxHeight="max-h-48"
            />
          </div>

          {/* Per-item destinations (F-d): spread items across different spaces.
              Only meaningful with 2+ items and a chosen trial target. */}
          {trialSpaceId && trialItemIds.length > 1 && (
            <div className="rounded-md border border-clay-border p-3">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={perItemTargets}
                  onChange={(e) => setPerItemTargets(e.target.checked)}
                  className="w-4 h-4 text-primary rounded-sm border-clay-border focus:ring-primary"
                />
                <span className="text-sm text-body">Send items to different spots</span>
              </label>
              {perItemTargets && (
                <div className="mt-3 space-y-2">
                  <p className="text-[0.8125rem] text-faint">
                    Each item defaults to {spaces.find((s) => s.id === trialSpaceId)?.name ?? "the chosen space"}. Change any that should go elsewhere.
                  </p>
                  {trialItemIds.map((itemId) => {
                    const gi = goal.items.find((g) => g.itemId === itemId);
                    return (
                      <div key={itemId} className="flex items-center gap-2">
                        <span className="text-xs text-body flex-1 min-w-0 truncate">{gi?.itemName ?? "Item"}</span>
                        <ArrowRight className="w-3 h-3 text-faint shrink-0" />
                        <select
                          value={itemTargets[itemId] ?? trialSpaceId}
                          onChange={(e) => setItemTargets((prev) => ({ ...prev, [itemId]: e.target.value }))}
                          className="text-xs px-2 py-1.5 border border-clay-border rounded-sm bg-white text-body focus:outline-none focus:border-primary max-w-[55%]"
                        >
                          {spaces.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {!tryMode ? (
            <>
              {/* PRIMARY (R-A1): one-tap plain move. No trial, no mode choice. */}
              <button
                onClick={handleMove}
                disabled={!trialSpaceId || trialItemIds.length === 0 || movingNow}
                className="w-full py-3 bg-primary text-white rounded-md text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed"
              >
                {movingNow ? "Moving…" : "Move it here"}
              </button>
              {/* Decided, but not moving it right now (§6b): records a planned
                  move so the location stays honest until it's actually moved. */}
              <button
                type="button"
                onClick={handleMoveLater}
                disabled={!trialSpaceId || trialItemIds.length === 0 || movingNow || planningLater}
                className="w-full py-2.5 rounded-md border border-clay-border bg-white text-body text-sm font-medium hover:border-primary/40 hover:text-amethyst-ink transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {planningLater ? "Saving…" : "Move later"}
              </button>
              {/* SUBORDINATE (R-A2): a real, labeled, legible control — not a
                  co-equal button and not gray fine print. Opts into the trial. */}
              <button
                type="button"
                onClick={() => { setTryMode(true); setTrialPermanent(false); }}
                disabled={movingNow}
                className="w-full flex items-center justify-center gap-2 py-2.5 text-sm text-body hover:text-amethyst-ink hover:bg-amethyst-soft/60 rounded-md transition-colors"
              >
                <Clock className="w-4 h-4 text-sage-deep" />
                Try it for 2 weeks instead
              </button>
            </>
          ) : (
            <>
              {/* Trial confirm (opted-in path). "Make this the home" adopts now;
                  "Start trial" begins the 14-day loop. */}
              <button
                onClick={handleCreateTrial}
                disabled={!trialSpaceId || trialItemIds.length === 0 || creatingTrial}
                className="w-full py-3 bg-primary text-white rounded-md text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed"
              >
                {creatingTrial ? (trialPermanent ? "Setting home…" : "Starting trial…") : (trialPermanent ? "Make this the home" : "Start 2-week trial")}
              </button>
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => setTrialPermanent((p) => !p)}
                  className="text-xs text-body hover:text-amethyst-ink transition-colors"
                >
                  {trialPermanent ? "← Back to a 2-week trial" : "Skip the trial — make this the home now"}
                </button>
                <button
                  type="button"
                  onClick={() => { setTryMode(false); setTrialPermanent(false); }}
                  className="text-xs text-faint hover:text-body transition-colors"
                >
                  Just move it
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Check-in modal */}
      <Modal open={!!showCheckIn} onClose={() => { setShowCheckIn(null); setOutcomeNote(""); setCheckInFlags([]); setCheckInTrialDetail(null); setCheckInStep("decide"); }} title="How's it going?">
        <div className="space-y-4">
          {/* Trial context: items, space, elapsed days */}
          {checkInTrialDetail && (
            <div className="p-3 rounded-md bg-amethyst-soft border border-clay-border space-y-2">
              {/* Day counter */}
              {checkInTrialDetail.startedAt && (() => {
                const startDate = new Date(checkInTrialDetail.startedAt);
                const now = new Date();
                const elapsedDays = Math.max(1, Math.round((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)));
                return (
                  <div className="flex items-center gap-2 text-xs text-body">
                    <Clock className="w-3.5 h-3.5 text-primary" />
                    <span className="font-medium"><span className="tabular-nums">{elapsedDays}</span> day{elapsedDays !== 1 ? "s" : ""} in {checkInTrialDetail.targetSpace?.name ?? "new spot"}</span>
                  </div>
                );
              })()}
              {/* Items involved */}
              {checkInTrialDetail.items.length > 0 && (
                <div className="flex items-start gap-2 text-xs text-body">
                  <Package className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                  <span>{checkInTrialDetail.items.map((i) => i.itemName).join(", ")}</span>
                </div>
              )}
            </div>
          )}

          {/* Main decision step */}
          {checkInStep === "decide" && (
            <>
              <p className="text-sm text-body">
                {checkInTrialDetail?.startedAt
                  ? "You've had this arrangement for a while now. What do you want to do?"
                  : "You've been trying this arrangement. What do you want to do?"}
              </p>

              {/* Show friction flags if any */}
              {checkInFlags.length > 0 && (
                <div className="p-3 rounded-md bg-warning-wash border border-clay-border">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-warning-text mb-2">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Household feedback (<span className="tabular-nums">{checkInFlags.length}</span>)
                  </div>
                  <div className="space-y-1">
                    {checkInFlags.map((flag) => (
                      <div key={flag.id} className="text-xs text-warning-text">
                        {flag.note ? `"${flag.note}"` : "A household member flagged this isn't working for them."}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs font-medium text-body mb-1 block">Notes (optional)</label>
                <textarea
                  value={outcomeNote}
                  onChange={(e) => setOutcomeNote(e.target.value)}
                  placeholder="What's working? What isn't?"
                  className="w-full px-4 py-3 border border-clay-border rounded-sm text-sm text-body placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <button
                  onClick={() => setCheckInStep("confirm-adopt")}
                  className="w-full py-3 bg-sage-deep text-white rounded-md text-sm font-medium hover:bg-sage-deep-hover transition-colors flex items-center justify-center gap-2"
                >
                  <CheckCircle2 className="w-4 h-4" />It's working — keep it
                </button>
                <button
                  onClick={() => setCheckInStep("confirm-revert")}
                  className="w-full py-3 bg-white text-body border border-clay-border rounded-md text-sm font-medium hover:bg-paper-raised transition-colors flex items-center justify-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />Not quite — put it back
                </button>
                <button
                  onClick={() => showCheckIn && handleExtend(showCheckIn)}
                  disabled={extendPending}
                  className="w-full py-2.5 text-faint text-xs font-medium hover:text-body transition-colors flex items-center justify-center gap-1 disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed"
                >
                  <Clock className="w-3.5 h-3.5" />{extendPending ? "Extending..." : "Need more time (extend 2 weeks)"}
                </button>
              </div>
            </>
          )}

          {/* Confirm adopt step */}
          {checkInStep === "confirm-adopt" && (
            <div className="space-y-3">
              <div className="p-3 rounded-md bg-sage-wash border border-clay-border">
                <p className="text-sm text-sage-deep font-medium mb-1">Keep this arrangement?</p>
                <p className="text-xs text-sage-deep">
                  {checkInTrialDetail?.items.length
                    ? `Keep ${checkInTrialDetail.items.length} item${checkInTrialDetail.items.length !== 1 ? "s" : ""} in ${checkInTrialDetail.targetSpace?.name ?? "their current spot"}? This becomes their permanent home.`
                    : "This becomes the permanent arrangement."}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => showCheckIn && handleAdopt(showCheckIn)}
                  disabled={adoptPending}
                  className="flex-1 py-3 bg-sage-deep text-white rounded-md text-sm font-medium hover:bg-sage-deep-hover transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <CheckCircle2 className="w-4 h-4" />{adoptPending ? "Keeping..." : "Yes, keep it"}
                </button>
                <button
                  onClick={() => setCheckInStep("decide")}
                  disabled={adoptPending}
                  className="py-3 px-4 bg-amethyst-soft text-amethyst-ink rounded-md text-sm font-medium hover:bg-amethyst-soft/70 transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed"
                >
                  Back
                </button>
              </div>
            </div>
          )}

          {/* Confirm revert step — shows item restore destinations */}
          {checkInStep === "confirm-revert" && (
            <div className="space-y-3">
              <div className="p-3 rounded-md bg-paper-raised border border-clay-border">
                {(() => {
                  const items = checkInTrialDetail?.items ?? [];
                  const unplaced = items.filter((i) => !i.originalSpaceName).length;
                  return (
                    <p className="text-sm text-ink font-medium mb-2">
                      Put {items.length > 0 ? `${items.length} item${items.length !== 1 ? "s" : ""}` : "items"} back?
                      {unplaced > 0 && (
                        <span className="block text-xs font-normal text-warning-text mt-0.5">
                          {unplaced} {unplaced === 1 ? "has" : "have"} no original spot and will be unassigned.
                        </span>
                      )}
                    </p>
                  );
                })()}
                {checkInTrialDetail?.items && checkInTrialDetail.items.length > 0 ? (
                  <div className="space-y-1.5">
                    {checkInTrialDetail.items.map((item) => (
                      <div key={item.itemId} className="flex items-center gap-2 text-xs">
                        {item.originalSpaceName ? (
                          <>
                            <span className="text-ink font-medium">{item.itemName}</span>
                            <ArrowRight className="w-3 h-3 text-faint shrink-0" />
                            <span className="text-body">{item.originalSpaceName}</span>
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="w-3 h-3 text-warning shrink-0" />
                            <span className="text-ink font-medium">{item.itemName}</span>
                            <span className="text-warning-text italic">→ will be unassigned (original space removed)</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-body">Items will be returned to their original locations.</p>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => showCheckIn && handleRevert(showCheckIn)}
                  disabled={revertPending}
                  className="flex-1 py-3 bg-white text-body border border-clay-border rounded-md text-sm font-medium hover:bg-paper-raised transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />{revertPending ? "Putting back..." : "Yes, put it back"}
                </button>
                <button
                  onClick={() => setCheckInStep("decide")}
                  disabled={revertPending}
                  className="py-3 px-4 bg-amethyst-soft text-amethyst-ink rounded-md text-sm font-medium hover:bg-amethyst-soft/70 transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed"
                >
                  Back
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Friction Flag modal (for non-owners) */}
      <Modal open={!!showFrictionFlag} onClose={() => { setShowFrictionFlag(null); setFrictionNote(""); }} title="Flag this trial">
        <div className="space-y-4">
          <p className="text-sm text-body">
            Let the trial owner know this arrangement isn't working for you. Your feedback will be shown at their next check-in.
          </p>
          <div>
            <label className="text-xs font-medium text-body mb-1 block">What's not working? (optional)</label>
            <textarea
              value={frictionNote}
              onChange={(e) => setFrictionNote(e.target.value)}
              placeholder="e.g., 'I can't reach the bowls in their new spot'"
              className="w-full px-4 py-3 border border-clay-border rounded-sm text-sm text-body placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
              rows={2}
            />
          </div>
          <button
            onClick={() => showFrictionFlag && handleSubmitFriction(showFrictionFlag)}
            disabled={flagging}
            className="w-full py-3 bg-warning text-white rounded-md text-sm font-medium hover:bg-warning-text transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Flag className="w-4 h-4" />{flagging ? "Sending..." : "Send feedback"}
          </button>
        </div>
      </Modal>

      {/* Goal → routine handoff (offered after a trial is kept) */}
      <Modal open={showRoutineHandoff} onClose={() => setShowRoutineHandoff(false)} title="Keep it this way?">
        <div className="space-y-4">
          <div className="flex items-start gap-2.5 p-3 rounded-md bg-success-wash border border-clay-border">
            <Zap className="w-4 h-4 text-success shrink-0 mt-0.5" />
            <p className="text-xs text-success-text">
              Nice — those items have a home now. Want a routine to keep it that way? This creates a
              standalone routine (independent of this goal).
            </p>
          </div>
          <div>
            <label className="text-xs font-medium text-body mb-1 block">Routine name</label>
            <input
              type="text"
              value={routineTitle}
              onChange={(e) => setRoutineTitle(e.target.value)}
              className="w-full px-4 py-3 border border-clay-border rounded-sm text-sm text-body placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              maxLength={200}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-body mb-1 block">How often?</label>
            <div className="flex gap-1.5 flex-wrap">
              {["daily", "weekly", "monthly", "quarterly", "yearly"].map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setRoutineFrequency(f)}
                  className={cn("px-3 py-1 rounded-full text-xs font-medium capitalize transition-colors",
                    routineFrequency === f ? "bg-primary text-white" : "bg-amethyst-soft text-amethyst-ink hover:bg-amethyst-soft/70")}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCreateRoutineFromGoal}
              disabled={!routineTitle.trim() || creatingRoutine}
              className="flex-1 py-3 bg-primary text-white rounded-md text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {creatingRoutine ? <><Loader2 className="w-4 h-4 animate-spin" />Creating…</> : "Create routine"}
            </button>
            <button
              onClick={() => setShowRoutineHandoff(false)}
              className="py-3 px-4 bg-amethyst-soft text-amethyst-ink rounded-md text-sm font-medium hover:bg-amethyst-soft/70 transition-colors"
            >
              No thanks
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
